import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createUser, updateUser } from '../../services/userService'
import type { UserResponse, BranchResponse, Role } from '../../types'
import '../../components/BranchForm/BranchFormModal.css'

interface Props {
  user?: UserResponse
  branches: BranchResponse[]
  roles: Role[]
  onClose: () => void
  onSaved: (u: UserResponse) => void
}

const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function UserFormModal({ user, branches, roles, onClose, onSaved }: Props) {
  const isEdit = !!user
  const { t } = useTranslation()

  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [login, setLogin] = useState(user?.login ?? '')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState(user?.email ?? '')
  const [phone, setPhone] = useState(user?.phoneNumber ?? '')
  const [branchId, setBranchId] = useState(user?.branchId ?? '')
  const [role, setRole] = useState(user?.role ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  function validate() {
    const e: Record<string, string> = {}
    if (!firstName.trim()) e.firstName = t('admin.user_form.required')
    if (!lastName.trim()) e.lastName = t('admin.user_form.required')
    if (!isEdit && !login.trim()) e.login = t('admin.user_form.required')
    if (!isEdit && !password.trim()) e.password = t('admin.user_form.required')
    if (!email.trim()) e.email = t('admin.user_form.required')
    else if (!EMAIL_REGEX.test(email)) e.email = t('admin.user_form.invalid_email')
    if (phone && !PHONE_REGEX.test(phone)) e.phone = t('admin.user_form.invalid_phone')
    if (!branchId) e.branchId = t('admin.user_form.required')
    if (!isEdit && !role) e.role = t('admin.user_form.required')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      let result: UserResponse
      if (isEdit) {
        result = await updateUser(user!.id, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phoneNumber: phone.trim(),
          branchId,
        })
      } else {
        result = await createUser({
          login: login.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phoneNumber: phone.trim(),
          branchId,
          role,
        })
      }
      onSaved(result)
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      if (code === 'LOGIN_ALREADY_EXISTS') setErrors({ login: t('admin.user_form.login_taken') })
      else if (code === 'EMAIL_ALREADY_EXISTS') setErrors({ email: t('admin.user_form.email_in_use') })
      else if (code === 'BRANCH_NOT_FOUND') setErrors({ branchId: t('admin.user_form.branch_not_found') })
      else setErrors({ _: t('admin.user_form.save_failed') })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{isEdit ? t('admin.user_form.title_edit') : t('admin.user_form.title_new')}</h2>
          <button className="modal__close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form className="modal__body" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label={t('admin.user_form.label_first_name')} error={errors.firstName}>
              <input className={`admin-input modal__input${errors.firstName ? ' admin-input--error' : ''}`}
                value={firstName} onChange={(e) => { setFirstName(e.target.value); clearErr('firstName') }}
                disabled={loading} autoFocus />
            </FormField>
            <FormField label={t('admin.user_form.label_last_name')} error={errors.lastName}>
              <input className={`admin-input modal__input${errors.lastName ? ' admin-input--error' : ''}`}
                value={lastName} onChange={(e) => { setLastName(e.target.value); clearErr('lastName') }}
                disabled={loading} />
            </FormField>
          </div>

          {!isEdit && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label={t('admin.user_form.label_login')} error={errors.login}>
                <input className={`admin-input modal__input${errors.login ? ' admin-input--error' : ''}`}
                  value={login} onChange={(e) => { setLogin(e.target.value); clearErr('login') }}
                  disabled={loading} autoComplete="off" />
              </FormField>
              <FormField label={t('admin.user_form.label_password')} error={errors.password}>
                <input className={`admin-input modal__input${errors.password ? ' admin-input--error' : ''}`}
                  type="password" value={password}
                  onChange={(e) => { setPassword(e.target.value); clearErr('password') }}
                  disabled={loading} autoComplete="new-password" />
              </FormField>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label={t('admin.user_form.label_email')} error={errors.email}>
              <input className={`admin-input modal__input${errors.email ? ' admin-input--error' : ''}`}
                type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); clearErr('email') }}
                disabled={loading} />
            </FormField>
            <FormField label={t('admin.user_form.label_phone')} error={errors.phone}>
              <input className={`admin-input modal__input${errors.phone ? ' admin-input--error' : ''}`}
                value={phone} placeholder="+7..."
                onChange={(e) => { setPhone(e.target.value); clearErr('phone') }}
                disabled={loading} />
            </FormField>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label={t('admin.user_form.label_branch')} error={errors.branchId}>
              <select className={`admin-select modal__input${errors.branchId ? ' admin-input--error' : ''}`}
                value={branchId} onChange={(e) => { setBranchId(e.target.value); clearErr('branchId') }}
                disabled={loading}>
                <option value="">{t('admin.user_form.select_branch')}</option>
                {branches.filter((b) => !b.archivedAt).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </FormField>

            {!isEdit && (
              <FormField label={t('admin.user_form.label_role')} error={errors.role}>
                <select className={`admin-select modal__input${errors.role ? ' admin-input--error' : ''}`}
                  value={role} onChange={(e) => { setRole(e.target.value); clearErr('role') }}
                  disabled={loading}>
                  <option value="">{t('admin.user_form.select_role')}</option>
                  {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                </select>
              </FormField>
            )}
          </div>

          {errors._ && <p className="modal__error">{errors._}</p>}

          <div className="modal__footer">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={onClose} disabled={loading}>
              {t('admin.user_form.cancel')}
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
              {loading ? t('admin.user_form.saving') : isEdit ? t('admin.user_form.save') : t('admin.user_form.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  function clearErr(key: string) {
    setErrors((p) => ({ ...p, [key]: '' }))
  }
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="modal__field">
      <label className="modal__label">{label}</label>
      {children}
      {error && <span className="modal__error">{error}</span>}
    </div>
  )
}
